import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, Button, Input, Form, Modal, Tooltip, Select, Flex } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';
import { DeleteFilled, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { ISite } from '@entities/site';

dayjs.extend(utc);

interface ISiteProps extends IBaseComponentProps {
    cityId: string | undefined;
    siteId: string | undefined;
    isEditMode: boolean;
    api: NotificationInstance;
    onSelect: (value: ISite | undefined) => void;
}

const Site = (props: ISiteProps) => {
    const { cityId, siteId, api, isEditMode, onSelect, serverFetch } = props;

    const { TextArea } = Input;

    const [siteForm] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [baseSites, setBaseSites] = useState<Array<ISite>>([]);
    const [sites, setSites] = useState<Array<ISite>>([]);
    const [currentSite, setCurrentSite] = useState<ISite>();
    const [siteOpen, setSiteOpen] = useState<boolean>(false);
    const [prevCityId, setPrevCityId] = useState<string | undefined>(undefined);

    const [refreshRequired, setRefreshRequired] = useState<boolean>();

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setBaseSites([]);

        if (!cityId) return;

        serverFetch(`sites`, { method: 'GET', queryParams: { cityId: cityId } })
            .then((data: any) => {
                setBaseSites(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения адресов разгрузки', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (cityId != prevCityId) {
            setPrevCityId(cityId);

            setRefreshRequired(true);

            if (prevCityId) onSelect(undefined);
        }
    }, [cityId]);

    useEffect(() => {
        setSites(baseSites);
    }, [baseSites]);

    useEffect(() => {
        onSetCurrent(siteId);
    }, [siteId, baseSites]);

    const onAdd = (entity: ISite) => {
        serverFetch(`sites`, { method: 'POST', bodyData: entity })
            .then((data: ISite) => {
                siteForm.resetFields();
                setSiteOpen(false);
                setRefreshRequired(true);

                let copy = [...baseSites];
                copy.push(data);
                setBaseSites(copy);

                onSelect(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка добавления адреса разгрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDelete = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`sites/${id}`, { method: 'DELETE' })
            .then(() => {
                onSelect(undefined);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления адреса разгрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetCurrent = (id: string | undefined) => {
        if (!id) {
            setCurrentSite(undefined);
            return;
        }

        const currentOption = baseSites.find((c) => c.id == id);
        setCurrentSite(currentOption);
    };

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <Select
                    disabled={!isEditMode}
                    style={{ width: isEditMode ? 'calc(100% - 30px)' : '100%' }}
                    value={currentSite?.id}
                    allowClear={true}
                    showSearch
                    onChange={(e) => {
                        if (!e) {
                            onSelect(undefined);
                            return;
                        }

                        const site = baseSites.find((c) => c.id == e);
                        onSelect(site);
                    }}
                    optionFilterProp='children'
                    filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                    filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                    options={sites.map((c) => {
                        return { value: c.id, label: c.address };
                    })}
                    optionRender={(option) => (
                        <Flex gap='small'>
                            <Tooltip title='Удалить' placement='left'>
                                <Button
                                    variant='link'
                                color='blue'
                                    size='small'
                                    icon={<DeleteFilled />}
                                    hidden={!currentSite || !isEditMode}
                                    onClick={() => {
                                        modal.confirm({
                                            title: `Удалить адрес разгрузки "${option.label}"?`,
                                            icon: <QuestionCircleOutlined />,
                                            okType: 'primary',
                                            okText: 'Удалить',
                                            cancelText: 'Отмена',
                                            onOk: () => {
                                                onDelete(option?.value as string);
                                            },
                                        });
                                    }}
                                />
                            </Tooltip>
                            <div style={{ textWrap: 'wrap' }}>{option.label}</div>
                        </Flex>
                    )}
                />
                {isEditMode && (
                    <Tooltip title='Добавить'>
                        <Button
                            disabled={!cityId}
                            icon={<PlusOutlined />}
                            onClick={() => setSiteOpen(true)}
                            hidden={!currentSite || !isEditMode}
                        />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                title='Добавить адрес разгрузки'
                open={siteOpen}
                okText='ОК'
                onOk={() => siteForm.submit()}
                onCancel={() => {
                    siteForm.resetFields();

                    setSiteOpen(false);
                }}
            >
                <Form colon={false} labelCol={{ span: 5 }} wrapperCol={{ span: 17 }} onFinish={onAdd} form={siteForm}>
                    <Form.Item hidden name='cityId' initialValue={cityId}>
                        <Input />
                    </Form.Item>
                    <Form.Item required label='Адрес' name='address' rules={[{ required: true, message: 'Укажите адрес разгрузки' }]}>
                        <TextArea
                            rows={2}
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default Site;
